import _ from 'lodash';
import { AmazonIds, AmazonLoginResponseType, CheckoutStages, PaymentAuthCodes } from '../constants/AppConstants';
import config from '../config';
import urls from '../urls';
import AmazonAPIClient from '../lib/AmazonAPIClient';
import OrderStore from '../stores/OrderStore';
import PaymentStore from '../stores/PaymentStore';
import { loadScript } from '../helpers/ExternalScript';
import { PaymentUnauthorizedError } from '../errors/Payment';
import { AmazonPayError } from '../errors/AmazonPayError';

export const ERROR_AMAZON_TIMEOUT = 'OTRL_AMAZON_SDK_TIMEOUT_ERROR';
const AMAZON_SCOPE = 'profile postal_code profile:user_id payments:widget payments:shipping_address payments:billing_address';
const SDK_CHECK_INTERVAL_MS = 100;
const SDK_FAILURE_TIMEOUT_MS = 10000;

const sellerId = config.get('amazonSellerId'); // Merchant ID
let accessToken = null;
let amazonOrderReferenceId = '';
let timeoutSdkId = null;
let intervalSdkId = null;
let options = {};
let csrfToken = null;

const waitForAmazonSdk = () => new Promise((resolve, reject) => {
    // clear any previous timers
    clearTimeout(timeoutSdkId);
    clearInterval(intervalSdkId);

    timeoutSdkId = setTimeout(() => {
        reject();
        throw ERROR_AMAZON_TIMEOUT;
    }, SDK_FAILURE_TIMEOUT_MS);
    intervalSdkId = setInterval(() => {
        if (typeof window.amazon !== 'undefined' && typeof window.amazon.Login !== 'undefined') {
            clearTimeout(timeoutSdkId);
            clearInterval(intervalSdkId);
            resolve();
        }
    }, SDK_CHECK_INTERVAL_MS);
});

const getCrossSiteRequestForgeryToken = () => {
    let token;

    if (window.crypto) {
        // 512-bits token
        token = btoa(window.crypto.getRandomValues(new Uint32Array(16)).join(''));
    } else {
        token = _.times(10, () => Math.random().toString(36).slice(2)).join('');
    }

    csrfToken = token;

    return token;
};

const AmazonPayService = {
    authorizeLogin: ({ useRedirect = false }) => {
        // Amazon supports two methods of authentication
        // 1. Popup - optimised for mobile and desktop web which works similar to PayPal pop-up modal window
        // 2. Redirect - required for mobile webviews inside native apps as they do not support pop-up browser windows
        const popup = !useRedirect;

        const loginOptions = {
            response_type: options.responseType,
            popup,
            scope: AMAZON_SCOPE,
            scope_data: {
                profile: { essential: true }
            },
            state: getCrossSiteRequestForgeryToken()
        };
        const redirectUrl = urls.getCurrentHostUrl(CheckoutStages.PAYMENT_AMAZON_PAY);

        // must defer here otherwise Payment.js won't get the state updated with Amazon Pay payment method
        _.defer(() => options.handleOnLoginComplete(false));

        if (useRedirect) {
            window.amazon.Login.authorize(loginOptions, redirectUrl);
        } else {
            window.amazon.Login.authorize(loginOptions, response => {
                if (!AmazonPayService.validateCsrf(response.state, csrfToken)) {
                    throw 'CSRF tokens mismatch error';
                }

                if (response.error) {
                    _.defer(() => options.handleOnError(response.error));
                } else {
                    accessToken = response.access_token;
                    _.defer(() => options.handleOnLoginComplete(true, accessToken));
                }
            });
        }
    },

    getAmazonOrderReferenceId() {
        return amazonOrderReferenceId;
    },

    async loadSdk({
        options: _options,
        handleOrderReferenceId,
        handleOnPaymentSelect,
        handleOnAddressSelect,
        handleOnLoginComplete,
        handleOnError
    }) {
        const defaults = {
            addressBook: false,
            addressBookId: AmazonIds.ADDRESS_BOOK,
            buttonColor: 'Gold',
            buttonId: AmazonIds.BUTTON,
            buttonSize: 'small',
            buttonType: 'PwA',
            handleOnAddressSelect,
            handleOnError,
            handleOnLoad: _.noop,
            handleOnLoginComplete,
            handleOnPaymentSelect,
            handleOrderReferenceId,
            isAddressReadOnly: false,
            isAmazonLogin: false,
            language: "en-UK",
            loginButtonID: AmazonIds.LOGIN_BUTTON,
            overrideAmazonOrderReferenceId: null,
            responseType: AmazonLoginResponseType.TOKEN,
            size: config.get('amazonPaySize'),
            walletId: AmazonIds.WALLET
        };

        options = { ...defaults, ..._options };

        const isButtonVisible = document.getElementById(options.buttonId) && document.getElementById(options.buttonId).hasChildNodes();
        const isAmazonLoginButtonVisible = document.getElementById(options.loginButtonId) && document.getElementById(options.loginButtonId).hasChildNodes();

        if (accessToken === null || !isButtonVisible || (options.isAmazonLogin && !isAmazonLoginButtonVisible)) {
            window.onAmazonLoginReady = otrlOnAmazonLoginReady;
            window.onAmazonPaymentsReady = otrlOnAmazonPaymentsReady({ handleOrderReferenceId, handleOnPaymentSelect, handleOnAddressSelect, handleOnLoginComplete, handleOnError });

            const amazonPaySdkUrl = config.get('amazonPaySdkUrls')[config.get('amazonPayEnvironment')];

            await loadScript(amazonPaySdkUrl);
            await waitForAmazonSdk();

            options.handleOnLoad();
        }
    },

    async cancelOrder(orderReferenceId) {
        const url = '/payments/amazonpay/order-reference';
        let error, response;

        const currentOrderId = OrderStore.getCurrent().get('id');

        const request = {
            amazonOrderReferenceId: orderReferenceId,
            orderId: currentOrderId
        };

        try {
            response = await AmazonAPIClient.delete(url, request);
        } catch (err) {
            error = err;
        }

        return ({ error, response });
    },

    async confirmOrder(orderReferenceId) {
        const url = '/payments/amazonpay/order-reference';
        let error, response;

        const currentOrderId = OrderStore.getCurrent().get('id');

        const request = {
            amazonOrderReferenceId: orderReferenceId,
            orderId: currentOrderId,
            successUrl: urls.getCurrentHostUrl(CheckoutStages.AMAZON_PAY_MFA_SUCCESS),
            failureUrl: urls.getCurrentHostUrl(CheckoutStages.AMAZON_PAY_MFA_FAILURE)
        };

        try {
            response = await AmazonAPIClient.post(url, request);
        } catch (err) {
            error = err;
        }

        return ({ error, response });
    },

    logout() {
        window.amazon.Login.logout();
        document.cookie = "amazon_Login_accessToken=;expires=Thu, 01 Jan 1970 00:00:00 GMT"; // delete cookie for redirection
    },

    async getOrderDetails(addressConsentToken = accessToken, orderReferenceId = this.getAmazonOrderReferenceId()) {
        const url = '/payments/amazonpay/order-address-details';
        let error, response;

        const currentOrderId = OrderStore.getCurrent().get('id');

        const request = {
            amazonOrderReferenceId: orderReferenceId,
            addressConsentToken,
            orderId: currentOrderId
        };

        try {
            response = await AmazonAPIClient.post(url, request);
        } catch (err) {
            error = err;
        }

        return ({ error, response });
    },

    async setOrderDetails(orderReferenceId = this.getAmazonOrderReferenceId()) {
        if (options.overrideAmazonOrderReferenceId) {
            return ({ response: 'InvalidPaymentMethod' });
        }

        const url = '/payments/amazonpay/order-details';
        let error, response;

        const currentOrderId = OrderStore.getCurrent().get('id');
        const amount = PaymentStore.getPaymentAmount();

        const request = {
            amazonOrderReferenceId: orderReferenceId,
            orderId: currentOrderId,
            amount
        };

        try {
            response = await AmazonAPIClient.post(url, request);
        } catch (err) {
            error = err;
        }

        return ({ error, response });
    },

    async authorizeOrder(orderReferenceId, addressConsentToken) {
        const url = '/payments/amazonpay/authorize';
        let response;

        const currentOrderId = OrderStore.getCurrent().get('id');
        const amount = PaymentStore.getPaymentAmount();

        const request = {
            amazonOrderReferenceId: orderReferenceId,
            orderId: currentOrderId,
            amount,
            addressConsentToken: addressConsentToken ? addressConsentToken : accessToken
        };

        try {
            response = await AmazonAPIClient.post(url, request);
        } catch (errorResponse) {
            throw new AmazonPayError(errorResponse);
        }

        return response;
    },

    async confirmOrderWithFlow(orderReferenceId) {
        window.OffAmazonPayments.initConfirmationFlow(sellerId, amazonOrderReferenceId, async confirmationFlow => {
            let stage = 'confirmation';
            let response = await AmazonPayService.confirmOrder(orderReferenceId);


            if (!response.error) {
                confirmationFlow.success();

                return response.response;
            }

            confirmationFlow.error();
            throw new PaymentUnauthorizedError(`AmazonPay error at stage '${stage}'.`, PaymentAuthCodes.API_ERROR);
        });
    },

    // eslint-disable-next-line no-shadow
    setCookies(accessToken) {
        document.cookie = `amazon_Login_accessToken=${accessToken};secure`;
    },

    validateCsrf(responseToken, originalToken) {
        return responseToken === originalToken;
    }
};

const onOrderReferenceCreate = handleOrderReferenceId => orderReference => {
    amazonOrderReferenceId = orderReference.getAmazonOrderReferenceId();

    if (typeof handleOrderReferenceId === 'function') {
        handleOrderReferenceId(amazonOrderReferenceId);
    }
};

const otrlOnAmazonLoginReady = () => {
    const clientId = config.get('amazonPayEnvironment') === 'sandbox' ? config.get('amazonPayClientIdSandbox') : config.get('amazonPayClientId');
    // eslint-disable-next-line no-console
    console.log('%c* ClientId', 'background: #fc0; color: #333', clientId);
    if (!clientId) {
        throw new Error('No Amazon Pay client ID set. Please try again');
    }

    window.amazon.Login.setClientId(clientId);
    window.amazon.Login.setUseCookie(true);
};

const otrlOnAmazonPaymentsReady = ({ handleOrderReferenceId, handleOnPaymentSelect, handleOnError }) => () => {
    // It's important that Amazon Login calls the createWalletWidget.
    // If it won't call it even if 'LwA' option is passed the Amazon SDK
    // will expect a real Amazon login instaead of a sandbox.

    const createWalletWidget = () => {
        // Use either the amazonOrderReferenceId from the current Amazon session
        // or use the overriden value (useful for InvalidPaymentMethod).
        const orderReferenceId = options.overrideAmazonOrderReferenceId ? options.overrideAmazonOrderReferenceId : amazonOrderReferenceId;

        // If there's an address book connect the wallet with the address book with the current (or overriden)
        // reference id. Otherwise for wallet-only payments don't pass any order reference and rely on
        // the new order reference created by Amazon Pay SDK.
        const passAmazonOrderReferenceId = options.addressBook || options.overrideAmazonOrderReferenceId
            ? { amazonOrderReferenceId: orderReferenceId }
            : null;

        new window.OffAmazonPayments.Widgets.Wallet({
            sellerId,
            scope: AMAZON_SCOPE,
            ...passAmazonOrderReferenceId,
            // If there's an address book present do not retrieve the order reference as both has to be the same.
            // Otherwise, for the wallet-only payments, run a callback to get the order reference.
            onOrderReferenceCreate: options.addressBook ? _.noop : onOrderReferenceCreate(handleOrderReferenceId),
            onPaymentSelect: function () {
                if (typeof handleOnPaymentSelect === 'function') {
                    handleOnPaymentSelect();
                }
            },
            design: {
                designMode: 'responsive'
            },
            onError: function (error) {
                handleOnError(error);
            }
        }).bind(options.walletId);
    };

    if (options.addressBook) {
        // Read-only mode is required for InvalidPaymentOnly authorisation errors so that
        // user can choose another payment method  but cannot alter the address.
        const passDisplayMode = options.isAddressReadOnly ? { displayMode: 'Read' } : null;
        // Same as above for the wallet case. If there's an order reference that should be retained
        // (for InvalidPaymentMethod we want to use the same order reference so that Amazon Pay
        // can display a better UI in their widgets) then use it otherwise let Amazon Pay SDK
        // create it for you via their onOrderReferenceCreate callback.
        const passOrderReference = options.overrideAmazonOrderReferenceId
            ? { amazonOrderReferenceId: options.overrideAmazonOrderReferenceId }
            : { onOrderReferenceCreate: onOrderReferenceCreate(handleOrderReferenceId) };

        new window.OffAmazonPayments.Widgets.AddressBook({
            sellerId,
            scope: AMAZON_SCOPE,
            ...passDisplayMode,
            ...passOrderReference,
            onAddressSelect: (address) => {
                // do stuff here like recalculate tax and/or shipping
                options.handleOnAddressSelect(address);
            },
            design: {
                designMode: 'responsive'
            },
            onError: function (error) {
                handleOnError(error);
            },
            onReady: createWalletWidget,
        }).bind(options.addressBookId);
    } else {
        createWalletWidget();
    }

    // var authRequest;

    // eslint-disable-next-line new-cap
    // window.OffAmazonPayments.Button("AmazonPayButton", sellerId, {
    //     type: options.buttonType,
    //     color: options.buttonColor,
    //     size: options.buttonSize,

    //     authorization: function () {
    //         const loginOptions = {
    //             scope: AMAZON_SCOPE,
    //             popup: true
    //         };

    //         const redirectUrl = 'https://' + config.get('bookingEngineHost') + urls.location(CheckoutStages.PAYMENT_AMAZON_PAY).pathname;

    //         window.amazon.Login.authorize(loginOptions, redirectUrl);
    //     },

    //     onError: function (error) {
    //         console.error(`Amazon Pay error: ${error.getErrorCode()} - ${error.getErrorMessage()}`);
    //     }
    // });
};

export default AmazonPayService;



// WEBPACK FOOTER //
// ./src/services/AmazonPayService.js